'use client';

import type { Interface } from '@unifyapps/defs/types/interface';
import type { SnackbarConfig } from '@unifyapps/defs/types/interface/components/Snackbar/type';
import { UA_THEME_VARS } from '@unifyapps/style/themes/consts';
import { clsx } from 'clsx';
import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useWindowSize } from 'react-use';
import { Toaster, toast } from 'sonner';
import { useContainerElementContext } from '../../providers/ContainerElementContext';
import { Toast } from '../Toast';
import { VISIBLE_TOASTS_COUNT } from './constants';
import { SnackbarContext } from './context';
import type { SnackbarContextType, SnackbarProps } from './types';
import {
  getAutoHideDuration,
  getSnackbarCoordinateStyle,
  getSnackbarPosition,
  getStartDecoratorComponent,
} from './utils';

const TOAST_SLOT_PROPS = {
  closeButton: {
    'data-test-id': 'snackbar-close',
  },
};

function SnackbarProvider(props: {
  children: React.ReactNode;
  config?: SnackbarConfig;
  className?: string;
  theme?: Interface['theme'];
  id: string; // must be unique for each SnackbarProvider
}) {
  const { children, className, config, theme, id } = props;
  const { width: windowWidth } = useWindowSize();

  const snackbarComponentConfig = config?.base ?? theme?.components?.snackbar?.base;
  const coordinatesStyles = useMemo(
    () => getSnackbarCoordinateStyle(snackbarComponentConfig),
    [snackbarComponentConfig],
  );
  const position = useMemo(
    () => getSnackbarPosition(snackbarComponentConfig),
    [snackbarComponentConfig],
  );
  const style = useMemo(() => {
    return {
      // NOTE: calculated using golden ratio of the screen width
      maxWidth: Math.max(windowWidth * 0.618 * 0.382, 500),
      minWidth: 300, // from JOY UI Snackbar
      ...coordinatesStyles,
    };
  }, [coordinatesStyles, windowWidth]);

  const containerElement = useContainerElementContext();

  // sonner options must not force a new showSnackbar on resize — consumers use showSnackbar in
  // useEffect deps; unstable identity re-ran those effects and duplicated toasts.
  const snackbarStyleRef = useRef(style);
  snackbarStyleRef.current = style;

  const showSnackbar = useCallback(
    (showSnackbarProps: SnackbarProps) => {
      const adaptedStartDecoratorComponent = getStartDecoratorComponent({
        color: showSnackbarProps.color || 'default',
        startDecoratorNode: showSnackbarProps.startDecoratorNode,
        startDecoratorComponent: showSnackbarProps.startDecoratorComponent,
      });

      const snackbarPosition = showSnackbarProps.position ?? position;
      const CustomToast = showSnackbarProps.slotOverrides?.toast;
      const customToastSlotProps = showSnackbarProps.slotOverrideProps?.toast ?? {};

      toast.custom(
        (toastId: string) =>
          CustomToast ? (
            <CustomToast
              id={toastId}
              key={toastId}
              onClose={() => toast.dismiss(toastId)}
              {...customToastSlotProps}
            />
          ) : (
            <Toast
              key={toastId}
              {...showSnackbarProps}
              className={clsx(UA_THEME_VARS, className, showSnackbarProps?.className)}
              color={showSnackbarProps.color || 'default'}
              data-color-scheme={showSnackbarProps.scheme || 'light'}
              id={toastId}
              onClose={() => toast.dismiss(toastId)}
              slotProps={TOAST_SLOT_PROPS}
              startDecoratorComponent={adaptedStartDecoratorComponent}
              startDecoratorNode={showSnackbarProps.startDecoratorNode}
            />
          ),
        {
          duration: getAutoHideDuration({
            autoHideDuration: showSnackbarProps.autoHideDuration,
            color: showSnackbarProps.color,
          }),
          style: snackbarStyleRef.current,
          position: snackbarPosition,
          toasterId: id,
          className: clsx('p-0', showSnackbarProps.className),
        },
      );
    },
    [position, className, id],
  );
  const contextValue = useMemo<SnackbarContextType>(
    () => ({
      showSnackbar,
      closeSnackbar: () => {
        // will close all the toasts
        toast.dismiss();
      },
    }),
    [showSnackbar],
  );

  const toaster = (
    <Toaster
      className={clsx('ua-snackbar-toaster', {
        '!mt-topbarHeight':
          (position === 'top-right' || position === 'top-left') && !('top' in style && style?.top),
      })}
      id={id}
      offset={'top' in style && style?.top ? 0 : 12}
      visibleToasts={VISIBLE_TOASTS_COUNT}
    />
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}

      {containerElement && config?.renderSnackbarInPortal
        ? createPortal(toaster, containerElement)
        : toaster}
    </SnackbarContext.Provider>
  );
}

export default SnackbarProvider;
